import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Switch,
  Tabs,
  Row,
  Col,
  Statistic,
  Progress,
  List,
  Badge,
  Tooltip,
  message,
  Popconfirm,
  Alert,
  Steps,
  Radio,
  Slider,
  Upload,
  Descriptions,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ApiOutlined,
  DollarOutlined,
  SettingOutlined,
  ReloadOutlined,
  ExportOutlined,
  ImportOutlined,
  FilterOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';

const { Option } = Select;

const ModelManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [costModalVisible, setCostModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [testProgress, setTestProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  // 处理函数
  const handleViewCostDetails = () => {
    setCostModalVisible(true);
    message.info('正在加载成本详情...');
  };

  // 新增模型
  const handleAddModel = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  // 编辑模型
  const handleEditModel = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  // 保存模型
  const handleSaveModel = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedRecord) {
        message.success('模型配置更新成功！');
      } else {
        message.success('模型配置创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除模型
  const handleDeleteModel = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(`已删除模型：${record.name}`);
    } catch (error) {
      message.error('删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 查看模型详情
  const handleViewModel = (record: any) => {
    setSelectedRecord(record);
    setCostModalVisible(true);
  };

  // 测试模型
  const handleTestModel = (record: any) => {
    setSelectedRecord(record);
    setTestProgress(0);
    setCurrentStep(0);
    setTestModalVisible(true);
  };

  // 复制模型配置
  const handleCopyModel = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      message.success(`已复制模型配置：${record.name}`);
    } catch (error) {
      message.error('复制失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 启用/禁用模型
  const handleToggleStatus = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const newStatus = record.status === 'active' ? 'inactive' : 'active';
      message.success(`模型已${newStatus === 'active' ? '启用' : '禁用'}`);
    } catch (error) {
      message.error('操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的模型');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已删除 ${selectedRowKeys.length} 个模型`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 运行模型测试
  const handleRunTest = async () => {
    setLoading(true);
    try {
      const steps = [
        { step: 0, title: '初始化模型', status: 'process' },
        { step: 1, title: '加载配置参数', status: 'process' },
        { step: 2, title: '执行推理测试', status: 'process' },
        { step: 3, title: '验证输出质量', status: 'process' },
        { step: 4, title: '生成测试报告', status: 'finish' },
      ];
      
      for (let i = 0; i < steps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setCurrentStep(i);
        setTestProgress((i + 1) * 20);
      }
      
      message.success('模型测试完成！');
    } catch (error) {
      message.error('测试失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const modelData = [
    {
      id: 1,
      name: 'GPT-4',
      provider: 'OpenAI',
      type: 'text-generation',
      status: 'active',
      apiKey: 'sk-***...***',
      endpoint: 'https://api.openai.com/v1/chat/completions',
      maxTokens: 4096,
      temperature: 0.7,
      costPerToken: 0.00003,
      usageCount: 12560,
      totalCost: 125.6,
      lastUsed: '2025-10-23 14:30',
    },
    {
      id: 2,
      name: 'GPT-3.5-turbo',
      provider: 'OpenAI',
      type: 'text-generation',
      status: 'active',
      apiKey: 'sk-***...***',
      endpoint: 'https://api.openai.com/v1/chat/completions',
      maxTokens: 4096,
      temperature: 0.7,
      costPerToken: 0.000002,
      usageCount: 25680,
      totalCost: 51.36,
      lastUsed: '2025-10-23 14:25',
    },
    {
      id: 3,
      name: '通义千问',
      provider: '阿里云',
      type: 'text-generation',
      status: 'active',
      apiKey: 'sk-***...***',
      endpoint: 'https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation',
      maxTokens: 2000,
      temperature: 0.8,
      costPerToken: 0.00002,
      usageCount: 8560,
      totalCost: 17.12,
      lastUsed: '2025-10-23 14:20',
    },
  ];

  const columns = [
    {
      title: '模型信息',
      key: 'modelInfo',
      width: 200,
      render: (_: any, record: any) => (
        <Space>
          <ApiOutlined style={{ fontSize: 24 }} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.provider}</div>
            <div>
              <Tag color="blue">{record.type}</Tag>
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '活跃' : '停用'}
        </Tag>
      ),
    },
    {
      title: '配置参数',
      key: 'config',
      width: 150,
      render: (_: any, record: any) => (
        <div>
          <div>Max Tokens: {record.maxTokens}</div>
          <div>Temperature: {record.temperature}</div>
          <div>成本: ${record.costPerToken}/token</div>
        </div>
      ),
    },
    {
      title: '使用统计',
      key: 'usage',
      width: 120,
      render: (_: any, record: any) => (
        <div>
          <div>调用次数: {record.usageCount.toLocaleString()}</div>
          <div>总成本: ${record.totalCost}</div>
        </div>
      ),
    },
    {
      title: '最后使用',
      dataIndex: 'lastUsed',
      key: 'lastUsed',
      width: 150,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewModel(record)}
            >
              详情
            </Button>
          </Tooltip>
          <Tooltip title="测试模型">
            <Button
              type="link"
              icon={<ExperimentOutlined />}
              onClick={() => handleTestModel(record)}
            >
              测试
            </Button>
          </Tooltip>
          <Tooltip title="编辑模型">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditModel(record)}
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="复制配置">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => handleCopyModel(record)}
            >
              复制
            </Button>
          </Tooltip>
          <Tooltip title={record.status === 'active' ? '禁用模型' : '启用模型'}>
            <Button
              type="link"
              icon={record.status === 'active' ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleStatus(record)}
              danger={record.status === 'active'}
            >
              {record.status === 'active' ? '禁用' : '启用'}
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个模型吗？"
            onConfirm={() => handleDeleteModel(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除模型">
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
              >
                删除
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const modelStats = [
    { title: '总模型数', value: 8, icon: <ApiOutlined /> },
    { title: '活跃模型', value: 6, icon: <ApiOutlined /> },
    { title: '今日调用', value: 3240, icon: <ApiOutlined /> },
    { title: '总成本', value: '$194.08', icon: <DollarOutlined /> },
  ];

  const costData = [
    { date: '2025-10-23', cost: 45.6, tokens: 1520000 },
    { date: '2025-10-22', cost: 52.3, tokens: 1743000 },
    { date: '2025-10-21', cost: 38.9, tokens: 1297000 },
    { date: '2025-10-20', cost: 41.2, tokens: 1373000 },
    { date: '2025-10-19', cost: 48.7, tokens: 1623000 },
  ];

  const handleEditSubmit = (values: any) => {
    console.log('保存模型配置:', values);
    setEditModalVisible(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1>大模型管理</h1>
        <p>管理和对接大模型，支持相关的推理和接口调用，实现多模型统一管理和成本控制</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {modelStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number">{stat.value}</div>
                <div className="stats-label">{stat.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* 模型列表 */}
        <Col xs={24} lg={16}>
          <Card
            title="模型列表"
            extra={
              <Space>
                <Tooltip title="导入模型配置">
                  <Button icon={<ImportOutlined />} onClick={() => message.info('导入功能开发中...')}>
                    导入
                  </Button>
                </Tooltip>
                <Tooltip title="导出模型配置">
                  <Button icon={<ExportOutlined />} onClick={() => message.info('导出功能开发中...')}>
                    导出
                  </Button>
                </Tooltip>
                <Tooltip title="成本分析">
                  <Button icon={<DollarOutlined />} onClick={() => setCostModalVisible(true)}>
                    成本分析
                  </Button>
                </Tooltip>
                <Tooltip title="添加模型">
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddModel}>
                    添加模型
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input
                  placeholder="搜索模型名称"
                  prefix={<SearchOutlined />}
                  style={{ width: 200 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Select 
                  placeholder="提供商" 
                  style={{ width: 120 }}
                  value={filterProvider}
                  onChange={setFilterProvider}
                >
                  <Option value="all">全部提供商</Option>
                  <Option value="OpenAI">OpenAI</Option>
                  <Option value="阿里云">阿里云</Option>
                  <Option value="百度">百度</Option>
                  <Option value="腾讯">腾讯</Option>
                </Select>
                <Select 
                  placeholder="状态" 
                  style={{ width: 100 }}
                  value={filterStatus}
                  onChange={setFilterStatus}
                >
                  <Option value="all">全部状态</Option>
                  <Option value="active">活跃</Option>
                  <Option value="inactive">停用</Option>
                </Select>
                <Tooltip title="刷新数据">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => message.info('数据已刷新')}
                  />
                </Tooltip>
                <Tooltip title="高级筛选">
                  <Button icon={<FilterOutlined />} />
                </Tooltip>
              </Space>
            </div>

            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={modelData}
              rowKey="id"
              scroll={{ x: 1200, y: 400 }}
              pagination={{
                total: modelData.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 个模型`,
              }}
            />

            {/* 批量操作 */}
            {selectedRowKeys.length > 0 && (
              <Card style={{ marginTop: 16 }}>
                <Space>
                  <span>已选择 {selectedRowKeys.length} 个模型</span>
                  <Tooltip title="批量启用">
                    <Button icon={<CheckCircleOutlined />} onClick={() => message.info('批量启用功能开发中...')}>
                      批量启用
                    </Button>
                  </Tooltip>
                  <Tooltip title="批量禁用">
                    <Button icon={<CloseCircleOutlined />} onClick={() => message.info('批量禁用功能开发中...')}>
                      批量禁用
                    </Button>
                  </Tooltip>
                  <Popconfirm
                    title={`确定要删除选中的 ${selectedRowKeys.length} 个模型吗？`}
                    onConfirm={handleBatchDelete}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="批量删除">
                      <Button icon={<DeleteOutlined />} danger>
                        批量删除
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </Card>
            )}
          </Card>
        </Col>

        {/* 成本统计 */}
        <Col xs={24} lg={8}>
          <Card title="成本统计" extra={<Button type="link" onClick={handleViewCostDetails}>查看详情</Button>}>
            <List
              size="small"
              dataSource={costData}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{item.date}</span>
                        <span style={{ color: '#f50' }}>${item.cost}</span>
                      </div>
                    }
                    description={`Token数: ${item.tokens.toLocaleString()}`}
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card title="模型使用率" style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>GPT-4</span>
                <span>45%</span>
              </div>
              <Progress percent={45} strokeColor="#52c41a" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>GPT-3.5-turbo</span>
                <span>35%</span>
              </div>
              <Progress percent={35} strokeColor="#1890ff" />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>通义千问</span>
                <span>20%</span>
              </div>
              <Progress percent={20} strokeColor="#faad14" />
            </div>
          </Card>
        </Col>
      </Row>

      {/* 编辑模型模态框 */}
      <Modal
        title={selectedRecord ? '编辑模型' : '添加模型'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button key="test">测试连接</Button>,
          <Button key="save" type="primary" onClick={() => form.submit()}>
            保存
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="模型名称"
                rules={[{ required: true, message: '请输入模型名称' }]}
              >
                <Input placeholder="如: GPT-4" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="provider"
                label="提供商"
                rules={[{ required: true, message: '请选择提供商' }]}
              >
                <Select placeholder="选择提供商">
                  <Option value="OpenAI">OpenAI</Option>
                  <Option value="阿里云">阿里云</Option>
                  <Option value="百度">百度</Option>
                  <Option value="智谱AI">智谱AI</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="endpoint"
            label="API端点"
            rules={[{ required: true, message: '请输入API端点' }]}
          >
            <Input placeholder="https://api.openai.com/v1/chat/completions" />
          </Form.Item>

          <Form.Item
            name="apiKey"
            label="API密钥"
            rules={[{ required: true, message: '请输入API密钥' }]}
          >
            <Input.Password placeholder="输入API密钥" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maxTokens"
                label="最大Token数"
                rules={[{ required: true, message: '请输入最大Token数' }]}
              >
                <InputNumber min={1} max={100000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="temperature"
                label="Temperature"
                rules={[{ required: true, message: '请输入Temperature' }]}
              >
                <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="costPerToken"
            label="每Token成本"
            rules={[{ required: true, message: '请输入每Token成本' }]}
          >
            <InputNumber min={0} step={0.000001} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="活跃" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 成本分析模态框 */}
      <Modal
        title="成本分析"
        open={costModalVisible}
        onCancel={() => setCostModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setCostModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="成本趋势" key="1">
            <div style={{ height: 300, background: '#f5f5f5', borderRadius: 8, padding: 16 }}>
              <div style={{ textAlign: 'center', padding: 50, color: '#999' }}>
                <DollarOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <div>成本趋势图表</div>
                <div style={{ fontSize: 12 }}>显示每日成本变化趋势</div>
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="模型对比" key="2">
            <Row gutter={16}>
              <Col span={8}>
                <Statistic title="GPT-4成本" value="$125.6" />
              </Col>
              <Col span={8}>
                <Statistic title="GPT-3.5成本" value="$51.36" />
              </Col>
              <Col span={8}>
                <Statistic title="通义千问成本" value="$17.12" />
              </Col>
            </Row>
            <div style={{ marginTop: 24 }}>
              <h4>成本占比</h4>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>GPT-4</span>
                  <span>64.7%</span>
                </div>
                <Progress percent={64.7} strokeColor="#52c41a" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>GPT-3.5-turbo</span>
                  <span>26.5%</span>
                </div>
                <Progress percent={26.5} strokeColor="#1890ff" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>通义千问</span>
                  <span>8.8%</span>
                </div>
                <Progress percent={8.8} strokeColor="#faad14" />
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="优化建议" key="3">
            <List
              dataSource={[
                {
                  title: '使用GPT-3.5-turbo替代GPT-4',
                  description: '对于简单任务，使用GPT-3.5-turbo可以节省80%的成本',
                  impact: '高',
                },
                {
                  title: '优化提示词长度',
                  description: '减少不必要的上下文，可以降低Token消耗',
                  impact: '中',
                },
                {
                  title: '启用缓存机制',
                  description: '对重复请求启用缓存，减少API调用次数',
                  impact: '高',
                },
              ]}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space>
                        <span>{item.title}</span>
                        <Tag color={item.impact === '高' ? 'red' : item.impact === '中' ? 'orange' : 'green'}>
                          {item.impact}影响
                        </Tag>
                      </Space>
                    }
                    description={item.description}
                  />
                </List.Item>
              )}
            />
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      {/* 编辑模型模态框 */}
      <Modal
        title={selectedRecord ? '编辑模型' : '新增模型'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            保存
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveModel}
        >
          <Form.Item
            name="name"
            label="模型名称"
            rules={[{ required: true, message: '请输入模型名称' }]}
          >
            <Input placeholder="请输入模型名称" />
          </Form.Item>
          
          <Form.Item
            name="provider"
            label="提供商"
            rules={[{ required: true, message: '请选择提供商' }]}
          >
            <Select placeholder="请选择提供商">
              <Option value="OpenAI">OpenAI</Option>
              <Option value="阿里云">阿里云</Option>
              <Option value="百度">百度</Option>
              <Option value="腾讯">腾讯</Option>
              <Option value="字节跳动">字节跳动</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="type"
            label="模型类型"
            rules={[{ required: true, message: '请选择模型类型' }]}
          >
            <Select placeholder="请选择模型类型">
              <Option value="text-generation">文本生成</Option>
              <Option value="text-embedding">文本嵌入</Option>
              <Option value="image-generation">图像生成</Option>
              <Option value="speech-to-text">语音转文本</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="apiKey"
            label="API密钥"
            rules={[{ required: true, message: '请输入API密钥' }]}
          >
            <Input.Password placeholder="请输入API密钥" />
          </Form.Item>
          
          <Form.Item
            name="baseUrl"
            label="API地址"
          >
            <Input placeholder="请输入API地址（可选）" />
          </Form.Item>
          
          <Form.Item
            name="maxTokens"
            label="最大Token数"
            initialValue={4096}
          >
            <InputNumber min={1} max={32000} style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="temperature"
            label="温度参数"
            initialValue={0.7}
          >
            <Slider min={0} max={2} step={0.1} />
          </Form.Item>
          
          <Form.Item
            name="enabled"
            label="启用状态"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 模型测试模态框 */}
      <Modal
        title="模型测试"
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setTestModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="test" 
            type="primary" 
            icon={<ExperimentOutlined />}
            onClick={handleRunTest}
            loading={loading}
          >
            运行测试
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Alert
              message="模型测试"
              description={`正在测试模型：${selectedRecord.name} (${selectedRecord.provider})`}
              type="info"
              style={{ marginBottom: 16 }}
            />
            
            <Steps
              current={currentStep}
              items={[
                { title: '初始化模型' },
                { title: '加载配置参数' },
                { title: '执行推理测试' },
                { title: '验证输出质量' },
                { title: '生成测试报告' },
              ]}
              style={{ marginBottom: 24 }}
            />
            
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={testProgress}
                format={() => `${testProgress}%`}
              />
            </div>
            
            {testProgress === 100 && (
              <Alert
                message="测试完成"
                description="模型测试已成功完成，所有功能正常。"
                type="success"
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ModelManagement;
